import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { MapPin, Phone, Mail, Facebook } from 'lucide-react';
import logo from '../../assets/icons/logo.png';
import backgroundImage from '../../assets/images/2.jpg';
import './Footer.css';

const FooterSection = styled.footer`
  background: url(${backgroundImage}) center/cover no-repeat;
  color: white;
  padding: 4rem 0 0;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(ellipse at center, transparent 0%, rgba(0, 0, 0, 0.4) 70%, rgba(0, 0, 0, 0.7) 100%);
    pointer-events: none;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  position: relative;
  z-index: 1;

  @media (max-width: 768px) {
    padding: 0 15px;
  }
`;

const FooterGrid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr;
  gap: 3rem;
  margin-bottom: 3rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
    margin-bottom: 2rem;
  }
`;

const FooterColumn = styled.div``;

const Logo = styled.div`
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
`;

const LogoImage = styled.img`
  height: 150px;
  width: auto;
  object-fit: contain;

  @media (max-width: 768px) {
    height: 120px;
  }
`;

const SubsidiaryInfo = styled.div`
  font-size: 0.85rem;
  color: #d1d5db;
  font-style: italic;
  line-height: 1.4;
`;

const FooterTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 1.5rem;
  color: white;

  @media (max-width: 768px) {
    font-size: 1.25rem;
    margin-bottom: 1rem;
  }
`;

const FooterLinks = styled.ul`
  list-style: none;
  padding: 0;
`;

const FooterLink = styled.li`
  margin-bottom: 0.75rem;
  position: relative;
  padding-left: 1rem;

  &::before {
    content: '•';
    position: absolute;
    left: 0;
    color: #FF0707;
    font-weight: bold;
  }
`;

const FooterLinkA = styled(Link)`
  color: #9ca3af;
  text-decoration: none;
  transition: color 0.3s ease;
  font-size: 0.9rem;
  word-wrap: break-word;
  overflow-wrap: break-word;

  &:hover {
    color: white;
  }

  @media (max-width: 768px) {
    font-size: 0.85rem;
  }
`;

const ContactInfo = styled.div`
  margin-bottom: 1rem;
`;

const ContactLabel = styled.div`
  font-size: 0.9rem;
  color: #9ca3af;
  margin-bottom: 0.5rem;
`;

const ContactDetail = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #e5e7eb;
  font-size: 1.1rem;
  font-weight: 600;
  margin-bottom: 1rem;
  word-wrap: break-word;
  overflow-wrap: break-word;

  @media (max-width: 768px) {
    font-size: 1rem;
    flex-wrap: wrap;
  }
`;

const ContactIcon = styled.div`
  color: #FF0707;
  display: flex;
  align-items: center;
`;

const BottomStripe = styled.div`
  background: #1D0C08;
  height: 120px;
  width: 100%;
  margin-top: 0;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 20px;
  gap: 2rem;

  @media (max-width: 768px) {
    height: 100px;
    padding: 0 15px;
    gap: 1rem;
    flex-direction: column;
    text-align: center;
  }
`;

const CopyrightText = styled.div`
  color: white;
  font-size: 0.9rem;
  font-weight: 400;

  @media (max-width: 768px) {
    font-size: 0.8rem;
  }
`;

const FacebookIcon = styled.div`
  width: 40px;
  height: 40px;
  background: #1877f2;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: #166fe5;
    transform: scale(1.05);
  }
`;

const Footer = () => {
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  return (
    <>
      <FooterSection>
        <Container>
          <FooterGrid>
            {/* Column 1: Company Information */}
            <FooterColumn>
              <Logo>
                <LogoImage src={logo} alt="Keyplot Investments Logo" />
              </Logo>
              <SubsidiaryInfo>
                KeyPlot Investments (Pty) Ltd is a subsidiary of<br />
                the United Africa Group (Pty) Ltd
              </SubsidiaryInfo>
            </FooterColumn>

            {/* Column 2: Navigation */}
            <FooterColumn>
              <FooterTitle>Navigation</FooterTitle>
              <FooterLinks>
                <FooterLink>
                  <FooterLinkA to="/" onClick={scrollToTop}>Home</FooterLinkA>
                </FooterLink>
                <FooterLink>
                  <FooterLinkA to="/kp-card" onClick={scrollToTop}>KP Card</FooterLinkA>
                </FooterLink>
                <FooterLink>
                  <FooterLinkA to="/parking-options" onClick={scrollToTop}>Parking Options</FooterLinkA>
                </FooterLink>
                <FooterLink>
                  <FooterLinkA to="/about" onClick={scrollToTop}>About Us</FooterLinkA>
                </FooterLink>
                <FooterLink>
                  <FooterLinkA to="/contact" onClick={scrollToTop}>Contact Us</FooterLinkA>
                </FooterLink>
                <FooterLink>
                  <FooterLinkA to="/careers" onClick={scrollToTop}>Careers</FooterLinkA>
                </FooterLink>
              </FooterLinks>
            </FooterColumn>

            {/* Column 3: Contact Us */}
            <FooterColumn>
              <FooterTitle>Contact Us</FooterTitle>
              <ContactInfo>
                <ContactLabel>Corporate Office Address:</ContactLabel>
                <ContactDetail>
                  <ContactIcon>
                    <MapPin size={16} />
                  </ContactIcon>
                  No. 6 Gutenberg Platz<br />
                  51-55 Werner List Street<br />
                  Windhoek, Namibia
                </ContactDetail>
              </ContactInfo>
              <ContactInfo>
                <ContactLabel>Customer Service:</ContactLabel>
                <ContactDetail>
                  <ContactIcon>
                    <Phone size={16} />
                  </ContactIcon>
                  +264 85 783 8206
                </ContactDetail>
                <ContactDetail>
                  <ContactIcon>
                    <Phone size={16} />
                  </ContactIcon>
                  +264 61 277 824
                </ContactDetail>
              </ContactInfo>
              <ContactInfo>
                <ContactDetail>
                  <ContactIcon>
                    <Mail size={16} />
                  </ContactIcon>
                  info@keyplot.com.na
                </ContactDetail>
              </ContactInfo>
            </FooterColumn>

            {/* Column 4: Discover */}
            <FooterColumn>
              <FooterTitle>Discover</FooterTitle>
              <FooterLinks>
                <FooterLink>
                  <FooterLinkA href="#privacy">Privacy Policy</FooterLinkA>
                </FooterLink>
                <FooterLink>
                  <FooterLinkA href="#device-pci">Device PCI PTS</FooterLinkA>
                </FooterLink>
                <FooterLink>
                  <FooterLinkA href="#certification">Certification</FooterLinkA>
                </FooterLink>
                <FooterLink>
                  <FooterLinkA href="#device-paycert">Device PayCert</FooterLinkA>
                </FooterLink>
                <FooterLink>
                  <FooterLinkA href="#kernal">Kernal Certification</FooterLinkA>
                </FooterLink>
                <FooterLink>
                  <FooterLinkA href="#device-emv">Device EMV CL L1</FooterLinkA>
                </FooterLink>
                <FooterLink>
                  <FooterLinkA href="#certification">Certification</FooterLinkA>
                </FooterLink>
              </FooterLinks>
            </FooterColumn>
          </FooterGrid>
        </Container>
      </FooterSection>
      <BottomStripe>
        <CopyrightText>KeyPlot Investments© 2025. All rights reserved.</CopyrightText>
        <FacebookIcon>
          <Facebook size={20} />
        </FacebookIcon>
      </BottomStripe>
    </>
  );
};

export default Footer; 